'use client';
import {
  UseFormRegister,
  FieldErrors,
  FieldValues,
  Path,
  FieldError,
} from 'react-hook-form';
import get from 'lodash/get';

import { FormError } from '@ui/FormError';
import Input from '@ui/Input';
import Label from '@ui/Label';
import { cn } from '@/lib/utils';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/20/solid';
import React from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

interface FormFieldProps<TFieldValues extends FieldValues> {
  name: Path<TFieldValues>;
  type: string;
  label?: string;
  placeholder: string;
  register: UseFormRegister<TFieldValues>;
  errors: FieldErrors<TFieldValues>;
  suffixIcon?: React.ReactNode;
  changeValueType?: (v: any) => void;
  serverErrors?: string[];
  hint?: string;
  readonly?: boolean;
  disabled?: boolean;
  labelClassNames?: string;
  inputClassNames?: string;
  tooltip?: string;
  prefixIcon?: React.ReactNode;
  numberStep?: string;
}

const InputField = <TFieldValues extends FieldValues>({
  name,
  type,
  label,
  placeholder,
  register,
  errors,
  suffixIcon,
  changeValueType,
  serverErrors,
  hint,
  disabled = false,
  readonly = false,
  labelClassNames = '',
  inputClassNames = '',
  tooltip,
  prefixIcon,
  numberStep,
}: React.PropsWithChildren<FormFieldProps<TFieldValues>>) => {
  const [showPassword, setShowPassword] = React.useState<boolean>(false);
  let strName = String(name);
  let isLocaleField = false;
  if (name.endsWith('.de') || name.endsWith('.en')) {
    strName = strName.slice(0, -3);
    isLocaleField = true;
  }

  const errorMessage = (get(errors, strName) as FieldError)?.message;
  const errorMessageLocales = (get(errors, name) as FieldError)?.message;

  const additionalAttrs: any = {};
  if (type === 'number') {
    additionalAttrs.min = 0;
  }
  if (numberStep) {
    additionalAttrs.step = numberStep;
  }

  return (
    <div className="relative mb-6 flex w-full flex-col items-start 2xl:mb-[26px]">
      {label && (
        <Label
          htmlFor={String(name)}
          className={cn('relative flex', labelClassNames)}
        >
          {label}
          {tooltip && (
            <>
              <InformationCircleIcon className="peer ml-2 h-5 w-5 cursor-pointer hover:scale-110 hover:text-primary" />
              <span className="absolute right-0 hidden w-max max-w-[300px] -translate-y-[25%] translate-x-[110%] rounded-lg bg-gray-800 px-5 py-3 text-sm text-gray-100 peer-hover:block">
                {tooltip}
              </span>
            </>
          )}
        </Label>
      )}
      {suffixIcon && (
        <span className="absolute right-2 top-10 animate-appear-shake cursor-pointer">
          {suffixIcon}
        </span>
      )}
      {prefixIcon && (
        <span className="absolute left-2 top-10 animate-appear-shake cursor-pointer">
          {prefixIcon}
        </span>
      )}

      <Input
        type={showPassword ? 'text' : type}
        id={String(name)}
        placeholder={placeholder}
        isError={Boolean(!!errorMessage)}
        readOnly={readonly}
        disabled={disabled}
        {...register(name, {
          setValueAs: (v) => (changeValueType ? changeValueType(v) : v),
        })}
        {...additionalAttrs}
        className={cn(
          disabled ? 'bg-gray-100' : '',
          prefixIcon ? 'pl-8' : '',
          inputClassNames
        )}
      />
      {!!hint && (
        <span className="mt-[5px] text-left text-xs text-paragraph-lite">
          {hint}
        </span>
      )}
      {strName && errorMessage && (
        <FormError message={errorMessage as string} />
      )}
      {isLocaleField && name && errorMessageLocales && (
        <FormError message={errorMessageLocales as string} />
      )}

      {serverErrors && serverErrors.length > 0
        ? serverErrors.map((e, i) => (
            <FormError key={i} message={e as string} />
          ))
        : null}

      {type === 'password' && (
        <span className="absolute right-2 top-[37px] cursor-pointer rounded p-1 text-paragraph-lite hover:bg-slate-200">
          {showPassword ? (
            <EyeSlashIcon
              className="h-5 w-5"
              onClick={() => setShowPassword(false)}
            />
          ) : (
            <EyeIcon
              className="h-5 w-5"
              onClick={() => setShowPassword(true)}
            />
          )}
        </span>
      )}
    </div>
  );
};

export default InputField;
